import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useMutation, useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import { BlurredContainerM3030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import { Fallback } from '../../components/utils/error-boundary';
import HideableComponent from '../../components/utils/hideable-component';
import { GraphqlOperations } from '../../logic/graphql/operations';
import { MessageListItem, Query, Subscription } from '../../logic/graphql/types/types';
import Presentation from '../../logic/redux/presentation';
import { ScreenSize, ScreenSizeFloor } from '../../styles/screen-sizes';
import MessageDetails from './message-details';
import MessageInfiniteList from './message-infinite-list';
import { FormattedMessage, useIntl } from 'react-intl';

const Container = styled(BlurredContainerM3030)`
  @media (max-width: 1280px) {
    padding: 20px 5px 10px 10px;
  }
`;

const MessageListContainer = styled.div`
  width: 290px;
  height: 100%;
  overflow: visible;
  display: flex;
  align-items: flex-start;
  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const DetailsContainer = styled.div`
  margin-left: 15px;
`;

const Messages = (): JSX.Element => {
  const intl = useIntl();
  const [messages, setMessages] = useState<MessageListItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedMessage, setSelectedMessage] = useState<MessageListItem | undefined>(undefined);
  const [hasSetFirst, setHadSetFirst] = useState(false);
  const [mobileSelected, setMobileSelected] = useState(false);

  const screenSize = useSelector(Presentation.select.selectScreenSize);
  const isMobileMode = screenSize <= ScreenSize.MD;

  const result = useQuery<Query>({
    query: GraphqlOperations.queries.get_messages.queryString,
    variables: GraphqlOperations.queries.get_messages.createVars(currentPage, 10),
    requestPolicy: 'network-only',
  })[0];
  const { data, fetching } = result;

  useEffect(() => {
    if (data?.get_messages?.page_count !== undefined && data.get_messages.page === currentPage) {
      setMessages([...messages.concat(data.get_messages.messages)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const readMessage = useMutation(GraphqlOperations.mutations.read_message.mutationString)[1];

  useEffect(() => {
    if (!hasSetFirst && messages && messages.length && currentPage === 1) {
      setHadSetFirst(true);
      const initialMessage = messages[0];
      setSelectedMessage(initialMessage);
      if (initialMessage.unread) {
        const variables = GraphqlOperations.mutations.read_message.createVars(initialMessage.id);
        readMessage(variables)
          .then((result) => {
            if (result && !result.error) {
              const notifs = [...messages];
              const notif = notifs.find((n) => n.id === initialMessage.id);
              if (notif) {
                notif.unread = false;
              }
              setMessages(notifs);
            }
            return true;
          })
          .catch((err) => console.log(err));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, hasSetFirst, currentPage]);

  const handleSubscription = (
    subscriptionMessages: MessageListItem[] = [],
    response: Subscription,
  ): MessageListItem[] => {
    if (response && response.new_message) {
      setMessages([response.new_message].concat(messages));
      return [response.new_message, ...subscriptionMessages];
    }
    return subscriptionMessages;
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.new_message.subscriptionString,
    },
    handleSubscription,
  );

  const handleReadAllClick = (): void => {
    const variables = GraphqlOperations.mutations.read_message.createVars(0);
    readMessage(variables)
      .then((result) => {
        if (result && !result.error) {
          const notifs = [...messages];
          notifs.forEach((n) => {
            n.unread = false;
          });
          setMessages(notifs);
        }
        return true;
      })
      .catch((err) => console.log(err));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,  @typescript-eslint/no-unused-vars
  const fetchNextPage = (startIndex: number, stopIndex: number): any => {
    if (startIndex / 10 >= currentPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleMessageClick = (message: MessageListItem): void => {
    setSelectedMessage(message);
    if (isMobileMode) {
      setMobileSelected(true);
    }
    if (message.unread) {
      const variables = GraphqlOperations.mutations.read_message.createVars(message.id);
      readMessage(variables)
        .then((result) => {
          if (result && !result.error) {
            const notifs = [...messages];
            const notif = notifs.find((n) => n.id === message.id);
            if (notif) {
              notif.unread = false;
            }
            setMessages(notifs);
          }
          return true;
        })
        .catch((err) => console.log(err));
    }
  };

  const handleMobileBackClick = (): void => {
    setMobileSelected(false);
  };

  const hasNextPage =
    result.data?.get_messages?.page_count !== undefined &&
    result.data?.get_messages?.page !== undefined &&
    result.data.get_messages.page_count > result.data.get_messages.page;

  const noContent = (
    <BlurredContainerM3030 className="flex flex-col">
      <p className="self-center text-12 py-6 text-gray" style={{ height: 'calc(100vh - 240px)' }}>
        <FormattedMessage id="messages.list.noContent" />
      </p>
    </BlurredContainerM3030>
  );

  const hasZeroItems =
    !result.fetching && !!result.data?.get_messages?.messages && messages.length === 0;

  const isShowingMobileDetails = selectedMessage && mobileSelected && isMobileMode;

  return (
    <DefaultScreen
      id="messages_screen"
      background={assets.images.messages_bg}
      title={isShowingMobileDetails ? '' : intl.formatMessage({ id: 'messages.title' })}
      loading={false}
      onBackClick={isShowingMobileDetails ? handleMobileBackClick : undefined}
    >
      {hasZeroItems ? (
        noContent
      ) : (
        <Container className={isShowingMobileDetails ? 'hidden' : ''}>
          <header className="flex flex-col items-end mb-3">
            <span
              className="mr-2 text-12 underline cursor-pointer lg:mr-0"
              onClick={handleReadAllClick}
            >
              <b>
                <FormattedMessage id="messages.list.markAllRead" />
              </b>
            </span>
          </header>
          <div className="flex flex-row">
            <MessageListContainer className="flex-none">
              <MessageInfiniteList
                hasNextPage={hasNextPage}
                isNextPageLoading={fetching}
                items={messages}
                loadNextPage={fetchNextPage}
                handleMessageClick={handleMessageClick}
                selectedMessage={selectedMessage}
              />
            </MessageListContainer>
            <HideableComponent showFrom={ScreenSizeFloor.LG}>
              <DetailsContainer className="flex-grow">
                {selectedMessage ? <MessageDetails message={selectedMessage} /> : null}
              </DetailsContainer>
            </HideableComponent>
          </div>
        </Container>
      )}
      {isShowingMobileDetails && selectedMessage ? (
        <MessageDetails message={selectedMessage} />
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(Messages, { fallback: Fallback });
